<template>
  <div class="applicant-table">
    <el-table
      v-if="rows.length"
      :data="rows"
      :row-key="row => row.triggerId"
      :default-sort="defaultSort"
      :border="false"
      :stripe="false"
      :show-header="true"
      :highlight-current-row="false"
      :cell-class-name="cellClassName"
      :header-cell-class-name="() => 'table__header-cell'"
      :row-class-name="rowClassName"
      :loading="loading"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="startedAt" column-key="entryStartedAt" sortable="custom" label="応募日時" min-width="170">
        <template #default="{ row }">
          <div class="date-cell">
            <div class="date-cell__line">{{ formatDate(row.startedAt) }}</div>
            <div class="date-cell__line">{{ formatTime(row.startedAt) }}</div>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="status" column-key="status" sortable="custom" label="応募状況" min-width="110">
        <template #default="{ row }">
          <span class="status-badge" data-test="status-badge" :class="statusClass(row.status)">
            {{ statusLabel(row.status) }}
          </span>
        </template>
      </el-table-column>

      <el-table-column prop="text" column-key="text" sortable="custom" label="投稿内容" min-width="190">
        <template #default="{ row }">
          <div class="text-cell" :title="row.text || '-'">{{ row.text || '-' }}</div>
        </template>
      </el-table-column>

      <el-table-column
        prop="applicantAccountId"
        column-key="account"
        sortable="custom"
        label="アカウント"
        min-width="200"
      >
        <template #default="{ row }">
          <div class="account" data-test="account-cell">
            <div class="avatar">
              <img :src="avatarSrc(row.applicantPictureUrl)" alt="" />
            </div>
            <div class="account__meta">
              <div class="account__name">
                <template v-if="row.applicantName">{{ row.applicantName }}</template>
                <el-tooltip v-else :enterable="false" placement="top">
                  <template #content>
                    <span class="tooltip-multiline">
                      アカウント名は応募から30分後に表示されます。<br />
                      未設定の場合は表示されません。
                    </span>
                  </template>
                  <span>-</span>
                </el-tooltip>
              </div>
              <div class="account__id">
                <template v-if="row.applicantScreenName">
                  <a
                    :href="accountProfileUrl(row.applicantScreenName)"
                    class="account__link"
                    target="_blank"
                    rel="noopener"
                  >
                    @{{ row.applicantScreenName }}
                  </a>
                </template>
                <template v-else>-</template>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>

      <el-table-column
        prop="applicantEntryCount"
        column-key="applicantEntryCount"
        sortable="custom"
        label="応募数"
        align="center"
        width="100"
      >
        <template #default="{ row }">
          <span class="number-cell">{{ row.applicantEntryCount }}</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="totalCampaignEntryCount"
        column-key="totalCampaignEntryCount"
        sortable="custom"
        label="累計参加数"
        align="center"
        width="120"
      >
        <template #default="{ row }">
          <span class="number-cell">{{ row.totalCampaignEntryCount }}</span>
        </template>
      </el-table-column>

      <el-table-column
        prop="totalCampaignWinningCount"
        column-key="totalCampaignWinningCount"
        sortable="custom"
        label="累計当選数"
        align="center"
        width="120"
      >
        <template #default="{ row }">
          <span class="number-cell">{{ row.totalCampaignWinningCount }}</span>
        </template>
      </el-table-column>

      <el-table-column align="center" width="140" label="">
        <template #default="{ row }">
          <div class="actions">
            <!-- メッセージ履歴は今はコメントアウト -->
            <!-- <el-button type="default" class="action-btn message-btn">
              <i class="mdi mdi-message-text" />
            </el-button> -->
            <el-tooltip :enterable="false" placement="top" content="Instagramで確認">
              <a v-if="profileUrl(row)" :href="profileUrl(row)" class="action-link" target="_blank" rel="noopener">
                <el-button type="default" class="action-btn open-btn">
                  <i class="mdi mdi-open-in-new" />
                </el-button>
              </a>
              <el-button v-else type="default" class="action-btn open-btn" disabled>
                <i class="mdi mdi-open-in-new" />
              </el-button>
            </el-tooltip>
            <el-tooltip :enterable="false" placement="top" content="メッセージを送る">
              <a v-if="messageUrl(row)" :href="messageUrl(row)" class="action-link" target="_blank" rel="noopener">
                <el-button type="default" class="action-btn send-btn">
                  <i class="mdi mdi-send" />
                </el-button>
              </a>
              <el-button v-else type="default" class="action-btn send-btn" disabled>
                <i class="mdi mdi-send" />
              </el-button>
            </el-tooltip>
          </div>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue'

import { ApplicantRow, SortBy } from '../api/getInstagramInstantwinApplicants'

export default defineComponent({
  name: 'InstagramInstantwinApplicantTable',
  props: {
    rows: {
      type: Array as () => ApplicantRow[],
      required: true
    },
    sortBy: {
      type: String as () => SortBy,
      required: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  emits: ['sort-change'],
  setup(props, { emit }) {
    const defaultSort = computed(() => {
      const key = props.sortBy.startsWith('-') ? props.sortBy.slice(1) : props.sortBy
      const order = props.sortBy.startsWith('-') ? 'descending' : 'ascending'
      return { prop: key === 'entryStartedAt' ? 'startedAt' : key, order }
    })

    const formatDate = (value: string | null) => {
      if (!value) return '-'
      const date = new Date(value)
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
    }

    const formatTime = (value: string | null) => {
      if (!value) return ''
      const date = new Date(value)
      const hh = `${date.getHours()}`.padStart(2, '0')
      const mm = `${date.getMinutes()}`.padStart(2, '0')
      return `${hh}:${mm}`
    }

    const statusLabel = (status: ApplicantRow['status']) => {
      switch (status) {
        case 'WIN':
          return '当選済'
        case 'PROCESSING':
          return '抽選前'
        case 'MISSING':
          return '離脱済'
        default:
          return '落選済'
      }
    }

    const statusClass = (status: ApplicantRow['status']) => {
      switch (status) {
        case 'WIN':
          return 'status--win'
        case 'PROCESSING':
          return 'status--processing'
        case 'MISSING':
          return 'status--missing'
        default:
          return 'status--lose'
      }
    }

    const avatarSrc = (url?: string | null) => {
      if (url) return url
      return require('@/client/assets/images/instagram_default_profile.png')
    }

    const profileUrl = (row: ApplicantRow) => {
      const handle = row.applicantScreenName || row.applicantAccountId
      if (!handle) return ''
      const normalized = handle.replace(/^@/, '')
      return normalized ? `https://www.instagram.com/${normalized}/` : ''
    }

    const accountProfileUrl = (screenName?: string | null) => {
      if (!screenName) return ''
      const normalized = screenName.replace(/^@/, '')
      return normalized ? `https://www.instagram.com/${normalized}/` : ''
    }

    const messageUrl = (row: ApplicantRow) => {
      const handle = row.applicantScreenName || row.applicantAccountId
      if (!handle) return ''
      const normalized = handle.replace(/^@/, '')
      return normalized ? `https://ig.me/m/${normalized}` : ''
    }

    const handleSortChange = (payload: {
      column?: { columnKey?: string; property?: string }
      prop?: string
      order?: 'ascending' | 'descending'
    }) => {
      if (props.loading) return
      const key = payload.column?.columnKey || payload.column?.property || payload.prop
      if (!key || !payload.order) return
      const normalizedKey = key === 'startedAt' ? 'entryStartedAt' : key
      const sortKey = payload.order === 'descending' ? (`-${normalizedKey}` as SortBy) : (normalizedKey as SortBy)
      emit('sort-change', sortKey)
    }

    const cellClassName = () => 'table__cell'
    const rowClassName = ({ rowIndex }: { rowIndex: number }) => (rowIndex % 2 === 0 ? 'row--even' : 'row--odd')

    return {
      defaultSort,
      formatDate,
      formatTime,
      statusLabel,
      statusClass,
      avatarSrc,
      accountProfileUrl,
      profileUrl,
      messageUrl,
      handleSortChange,
      cellClassName,
      rowClassName
    }
  }
})
</script>

<style lang="scss" scoped>
.applicant-table {
  width: 100%;
}

.table__header-cell {
  padding: 12px 14px !important;
  font-size: 13px;
  font-weight: 600;
  color: #5f6368;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}

.table__cell {
  padding: 14px !important;
  font-size: 14px;
  font-weight: 400;
  color: #3f434a;
  border-bottom: 1px solid #ededed;
}

.row--even .table__cell {
  background: #fff;
}

.row--odd .table__cell {
  background: #fafafa;
}

.date-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.date-cell__line {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  color: #303238;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: auto;
  min-width: 0;
  padding: 6px 4px;
  font-size: 14px;
  line-height: 1.1;
  color: #fff;
  border-radius: 4px;
}

.status--win {
  background: #e46f93;
}

.status--processing {
  background: #f6b042;
}

.status--missing {
  background: #9fa1a6;
}

.status--lose {
  background: #b5b7bc;
}

.text-cell {
  overflow: hidden;
  text-overflow: ellipsis;
  color: #3f434a;
  white-space: nowrap;
}

.account {
  display: flex;
  gap: 12px;
  align-items: center;
}

.avatar {
  display: inline-flex;
  flex: 0 0 36px;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  overflow: hidden;
  font-weight: 700;
  color: #4a4d52;
  background: #e5e7eb;
  border-radius: 50%;
}

.avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.account__meta {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 0;
  min-width: 0;
}

.account__name {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.2;
  color: #32363f;
  white-space: nowrap;
}

.account__id {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  line-height: 1.1;
  color: #7a7f85;
  white-space: nowrap;
}

.account__link {
  display: inline-block;
  max-width: 100%;
  color: inherit;
  text-decoration: none;
}

.account__link:hover {
  text-decoration: underline;
}

.tooltip-multiline {
  display: inline-block;
  line-height: 1.4;
  white-space: normal;
}

.number-cell {
  display: inline-block;
  font-size: 14px;
  font-weight: 500;
  color: #32363f;
}

.actions {
  display: inline-flex;
  gap: 4px;
}

.action-link {
  display: inline-flex;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  min-width: 0;
  height: 40px;
  padding: 0;
  line-height: 0;
  color: #4c5056;
  background: #fff;
  border: 1px solid #d0d4db;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgb(0 0 0 / 8%);
}

.action-btn :deep(.el-button__inner) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 0;
  line-height: 0;
}

.action-btn :deep(i) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin: 0;
  font-size: 18px;
  line-height: 1;
}

.message-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.open-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.send-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
